import { UserInfoCard } from '@/renderer/components/common/UserInfoCardInline';
import { formatDateTime, STATUS_BADGE } from '@/renderer/lib/utils';
import { AccommodationBooking } from '@/renderer/types/accommodation';
import { useAppSelector } from '@hooks/useRedux';
import { selectAccommodationProperty } from '@store/accommodation';
import { MoreHorizontal } from 'lucide-react';
import React, { FC } from 'react';

interface BookingsTableProps {
  bookings: AccommodationBooking[]; // Replace with actual booking type
  allPageSelected: boolean;
  selected: Set<string>;
  onToggleRow: (id: string) => void;
  onToggleAll: () => void;
  onRowClick?: (
    e: React.MouseEvent<HTMLTableRowElement>,
    booking: AccommodationBooking,
  ) => void;
}

export const BookingsTable: FC<BookingsTableProps> = ({
  bookings,
  onToggleRow,
  onToggleAll,
  onRowClick,
  allPageSelected,
  selected,
}) => {
  const property = useAppSelector(selectAccommodationProperty);
  const checkInTime = property?.check_in_time ?? '14:00';
  const checkOutTime = property?.check_out_time ?? '11:00';
  return (
    <div className="overflow-x-auto max-h-[calc(100vh-260px)] overflow-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/60">
            {/* Checkbox */}
            <th className="w-10 px-4 py-3">
              <input
                type="checkbox"
                aria-label="Select all on page"
                checked={allPageSelected}
                onChange={onToggleAll}
                className="rounded border-gray-300"
                data-no-preview
              />
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500">
              Booking No
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500">
              Type &amp; Room
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500">
              Guest Name
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500">
              Check In
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500">
              Check Out
            </th>
            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500">
              Paid Amount
            </th>
            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500">
              Due Amount
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500">
              Status
            </th>
            <th className="w-10 px-3 py-3" aria-label="Actions" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {bookings.map((b) => {
            const amountPaid = b.amount_paid ?? 0;
            const balance = Math.max(0, b.total_amount - amountPaid);
            const badge = STATUS_BADGE[b.status];
            const isSelected = selected.has(b.id);
            const rooms = b.booking_rooms ?? [];
            const firstRoomDetails = rooms[0]?.room;

            return (
              <tr
                key={b.id}
                onClick={(e) => onRowClick?.(e, b)}
                className={[
                  'cursor-pointer transition-colors hover:bg-gray-50/80',
                  isSelected ? 'bg-pos-primary/5' : '',
                ].join(' ')}
              >
                {/* Checkbox */}
                <td
                  className="w-10 px-4 py-3"
                  data-no-preview="true"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    aria-label={`Select booking ${b.id.slice(0, 8).toUpperCase()}`}
                    checked={isSelected}
                    onChange={() => onToggleRow(b.id)}
                    className="rounded border-gray-300"
                  />
                </td>

                {/* Booking No */}
                <td className="px-3 py-3">
                  <span className="font-mono text-xs font-semibold text-gray-700">
                    #{b.id.slice(0, 8).toUpperCase()}
                  </span>
                </td>

                {/* Type & Room */}
                <td className="px-3 py-3">
                  <div className="flex flex-col gap-1">
                    {/* {roomLabel(b)} */}
                    <p className="text-xs font-medium text-black">
                      {firstRoomDetails?.room_type?.name ?? 'Room'}
                    </p>
                    <p className="text-xs font-medium text-gray-400">
                      #{firstRoomDetails?.room_number ?? 'Room'}
                    </p>
                  </div>
                </td>

                {/* Guest name with avatar */}
                <td className="px-3 py-3">
                  <UserInfoCard
                    firstName={b.client?.first_name ?? ''}
                    lastName={b.client?.last_name ?? ''}
                    email={b.client?.email ?? ''}
                  />
                </td>

                {/* Check-in */}
                <td className="px-3 py-3 text-xs text-gray-600">
                  {formatDateTime(b.check_in, checkInTime)}
                </td>

                {/* Check-out */}
                <td className="px-3 py-3 text-xs text-gray-600">
                  {formatDateTime(b.check_out, checkOutTime)}
                </td>

                {/* Paid Amount */}
                <td className="px-3 py-3 text-right text-xs font-semibold text-gray-900">
                  {b.currency}{' '}
                  {amountPaid.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>

                {/* Due Amount */}
                <td className="px-3 py-3 text-right text-xs font-semibold">
                  {balance > 0 ? (
                    <span className="text-red-500">
                      {b.currency}{' '}
                      {balance.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  ) : (
                    <span className="text-green-600">—</span>
                  )}
                </td>

                {/* Status */}
                <td className="px-3 py-3">
                  <span
                    className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${badge.classes}`}
                  >
                    {badge.label}
                  </span>
                </td>

                {/* Action menu */}
                <td
                  className="px-3 py-3"
                  data-no-preview="true"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <MoreHorizontal size={14} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
